import React, { FC, useEffect, useState, useMemo } from 'react';
import { Form, Formik, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import {
  FormikCheckbox,
  FormikTextInput,
  FormikSearchSelect,
  FormikToggle,
  PageSpinner
} from '@td-client-frontend/common';
import { useAxios } from '../../../context/AxiosContext';
import { Button } from '../../../components/Button';
import { toast } from 'react-toastify';
import { LoaderConfig, Template } from '../../../@types/Templates';

interface modalProps {
  template?: Template;
  onClose: () => void;
  onSubmit: (obj: any) => void;
}

interface Flows {
  label: string;
  value: string;
  config: LoaderConfig;
}

const validationSchema = Yup.object({
  hasHeaders: Yup.bool(),
  ContracteeOrgId: Yup.number().typeError('Should be numeric').required('The field is required'),
  AllowedGroupSettingsId: Yup.number().typeError('Should be numeric').required('The field is required'),
  loaderFlow: Yup.string().required('The field is required'),
  autoApprove: Yup.bool()
});

const TemplateSettingsModalContent: FC<modalProps> = ({ template, onClose, onSubmit }) => {
  const initialValues = useMemo(
    () => ({
      hasHeaders: template?.config ? template.config.hasHeaders : true,
      ContracteeOrgId: template?.defaults?.global.ContracteeOrgId || '',
      AllowedGroupSettingsId: template?.defaults?.global.AllowedGroupSettingsId || '',
      loaderFlow: template?.flow?.name || 'Flow1B',
      autoApprove: template?.config ? template.config.autoApprove : false
    }),
    [template]
  );

  const { authAxios } = useAxios();
  const [flows, setFlows] = useState<Flows[]>([]);

  useEffect(() => {
    (async () => {
      try {
        const response = await authAxios.get('/reseller/template/flows');
        const data: Flows[] = [];
        for (const flow of response.data) {
          data.push({ label: flow.name, value: flow.name, config: flow });
        }
        setFlows(data);
      } catch (error) {
        toast.error(error.message, {
          position: 'top-center',
          hideProgressBar: true,
          pauseOnHover: true,
          draggable: true
        });
        onClose();
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTooltipShow = (isValid: boolean) => {
    if (!isValid) {
      toast.error('Please enter all required fields', {
        position: 'top-center',
        hideProgressBar: true,
        pauseOnHover: true,
        draggable: true
      });
    }
  };

  const handleSubmit = (
    { loaderFlow, AllowedGroupSettingsId, ContracteeOrgId, autoApprove, hasHeaders }: any,
    { setSubmitting }: FormikHelpers<any>
  ) => {
    setSubmitting(true);
    onSubmit({
      ...template,
      flow: flows.find((flow) => loaderFlow === flow.value)?.config,
      defaults: {
        ...template?.defaults,
        global: {
          AllowedGroupSettingsId,
          ContracteeOrgId
        }
      },
      config: {
        ...template?.config,
        autoApprove,
        hasHeaders
      }
    });
    setSubmitting(false);
    onClose();
  };

  if (!flows.length) {
    return (
      <div className="flex justify-center items-center flex-grow">
        <PageSpinner />
      </div>
    );
  }

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      validateOnBlur
      validateOnChange
      validateOnMount
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ isValid, isSubmitting }) => {
        return (
          <Form className="flex-grow">
            <div className="p-6 grid grid-cols-2 gap-y-4 gap-x-6">
              <FormikCheckbox name="hasHeaders" label="Include Headers">
                Includes
              </FormikCheckbox>
              {/* @ts-ignore */}
              <FormikSearchSelect label="Loader Flow" isRequired name="loaderFlow" options={flows} isFiltered={false} />
              <FormikTextInput
                label="Contractee Org Id"
                isRequired
                name="ContracteeOrgId"
                type="text"
                placeholder="Contractee Org Id"
              />
              <FormikTextInput
                label="Allowed Group Settings Id"
                isRequired
                name="AllowedGroupSettingsId"
                type="text"
                placeholder="Allowed Group Settings Id"
              />
              <FormikToggle name="autoApprove" label="Auto Approve" />
            </div>
            <div className="flex items-center justify-end p-6 rounded-b-lg sm:px-6 sm:flex bg-gray-10 border-t-1 border-gray-20">
              <div className="flex gap-4">
                <Button kind="secondary" title="Cancel" onClick={onClose} />
                <span onMouseEnter={() => handleTooltipShow(isValid)}>
                  <Button kind="primary" title="Save" disabled={isSubmitting || !isValid} type="submit" />
                </span>
              </div>
            </div>
          </Form>
        );
      }}
    </Formik>
  );
};

export default TemplateSettingsModalContent;
